import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  MessageCircle, Users, DollarSign, Send, ArrowLeft,
  Home, Ticket, Heart, Settings, Clock, Share2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TipArtistSheet } from '@/components/modals/TipArtistSheet';
import { 
  mockEvents, mockEventChatMessages, mockTipHistory, 
  getLivestreamStatus, getDebugUI, mockArtists 
} from '@/lib/mockData';
import { formatDate, formatTime, getTimeRemaining } from '@/lib/utils';
import { toast } from 'sonner';

// Mobile Bottom Navigation
const MobileBottomNav = ({ activePath }) => {
  const navItems = [
    { to: '/', icon: Home, label: 'Home', key: 'home' },
    { to: '/me/tickets', icon: Ticket, label: 'Tickets', key: 'tickets' },
    { to: '/me/wishlist', icon: Heart, label: 'Wishlist', key: 'wishlist' },
    { to: '#', icon: Settings, label: 'Settings', key: 'settings' },
  ];

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-bottom" data-testid="mobile-bottom-nav">
      <div className="flex items-center justify-around h-16">
        {navItems.map(({ to, icon: Icon, label, key }) => {
          const isActive = activePath === key;
          return (
            <Link
              key={key}
              to={to}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`nav-${key}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-xs mt-1">{label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

// Countdown Timer Component
const CountdownTimer = ({ targetDate, size = 'large' }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeRemaining(targetDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  if (timeLeft.total <= 0) {
    return null;
  }

  const isLarge = size === 'large';

  return (
    <div className="text-center" data-testid="countdown-timer">
      <p className={`text-muted-foreground mb-3 ${isLarge ? 'text-sm' : 'text-xs'}`}>Event Starts In</p>
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        {[
          { value: timeLeft.days, label: 'Days' },
          { value: timeLeft.hours, label: 'Hours' },
          { value: timeLeft.minutes, label: 'Mins' },
          { value: timeLeft.seconds, label: 'Secs' },
        ].map((item, i) => (
          <React.Fragment key={item.label}>
            {i > 0 && <span className={`${isLarge ? 'text-2xl' : 'text-lg'} text-muted-foreground`}>:</span>}
            <div className="text-center">
              <span className={`${isLarge ? 'text-3xl sm:text-4xl' : 'text-xl sm:text-2xl'} font-bold text-white tabular-nums`}>
                {String(item.value).padStart(2, '0')}
              </span>
              <span className={`${isLarge ? 'text-xs' : 'text-[10px]'} text-primary block`}>{item.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

// Debug UI Control Panel
const DebugUIPanel = ({ forcedStatus, onStatusChange, isVisible }) => {
  if (!isVisible) return null;

  return (
    <div 
      className="fixed top-20 left-4 z-50 bg-gray-900/95 border border-yellow-500 rounded-lg p-3 shadow-xl"
      data-testid="debug-ui-panel"
    >
      <p className="text-yellow-500 text-xs font-bold mb-2">DEBUG MODE</p>
      <div className="flex flex-col gap-1">
        {['UPCOMING', 'LIVE', 'ENDED'].map(status => (
          <button
            key={status}
            onClick={() => onStatusChange(status)}
            className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${
              forcedStatus === status 
                ? 'bg-yellow-500 text-black' 
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
            data-testid={`debug-force-${status.toLowerCase()}`}
          >
            Force {status}
          </button>
        ))}
        <button
          onClick={() => onStatusChange(null)}
          className="px-3 py-1.5 text-xs rounded font-medium bg-gray-700 text-white hover:bg-gray-600 mt-1"
          data-testid="debug-force-auto"
        >
          Auto (Time-based)
        </button>
      </div>
    </div>
  );
};

// UPCOMING State Component
const UpcomingState = ({ event, timeLeft }) => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="livestream-upcoming">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-secondary rounded-lg">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white truncate">{event.title}</h1>
            <p className="text-xs text-muted-foreground">{formatDate(event.date)} at {formatTime(event.date)}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-16 pb-20 lg:pb-8 flex flex-col items-center justify-center px-4">
        {/* Event Image */}
        <div className="w-full max-w-md mb-8">
          <div className="relative aspect-video rounded-2xl overflow-hidden">
            <img 
              src={event.coverImage || event.image} 
              alt={event.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <span className="inline-flex items-center gap-1.5 bg-primary/90 px-3 py-1 rounded-full text-xs font-semibold text-primary-foreground">
                <Clock className="w-3 h-3" />
                UPCOMING
              </span>
            </div>
          </div>
        </div>

        {/* Countdown */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-8 w-full max-w-md">
          <CountdownTimer targetDate={event.date} size="large" />
          
          <p className="text-sm text-muted-foreground text-center mt-6 max-w-sm mx-auto">
            You will be able to join using the &quot;Join Livestream&quot; button below once the event begins.
          </p>

          <Button 
            disabled
            className="w-full mt-6 h-12 bg-secondary text-muted-foreground cursor-not-allowed"
            data-testid="join-livestream-disabled"
          >
            <Play className="w-4 h-4 mr-2" />
            JOIN LIVESTREAM
          </Button>
        </div>

        {/* Artists */}
        {event.artists && event.artists.length > 0 && (
          <div className="mt-8 w-full max-w-md">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Performing Artists</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {event.artists.map(artist => (
                <Link 
                  key={artist.id} 
                  to={`/artists/${artist.id}`}
                  className="flex flex-col items-center flex-shrink-0 group"
                >
                  <img 
                    src={artist.image} 
                    alt={artist.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-border group-hover:border-primary transition-colors"
                  />
                  <span className="text-xs text-muted-foreground mt-1 max-w-[60px] truncate group-hover:text-primary transition-colors">{artist.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>

      <MobileBottomNav activePath="tickets" />
    </div>
  );
};

// LIVE State Component  
const LiveState = ({ event, onOpenTipSheet, onTipSent }) => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Load chat messages from localStorage in debug mode, otherwise use mock data
  const isDebugMode = getDebugUI();
  const storageKey = `exit88_chat_${event.id}`;
  
  const [chatMessages, setChatMessages] = useState(() => {
    if (isDebugMode) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse stored chat messages');
        }
      }
    }
    return mockEventChatMessages[event.id] || mockEventChatMessages['evt-006'] || [];
  });
  
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(true);
  const chatContainerRef = useRef(null);
  const videoContainerRef = useRef(null);

  // Persist chat messages to localStorage in debug mode
  useEffect(() => {
    if (isDebugMode && chatMessages.length > 0) {
      localStorage.setItem(storageKey, JSON.stringify(chatMessages));
    }
  }, [chatMessages, isDebugMode, storageKey]);

  // Scroll chat to bottom when messages update
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSendMessage = useCallback(() => {
    if (!newMessage.trim()) return;
    
    const msg = {
      id: `msg-user-${Date.now()}`,
      userId: 'user-001',
      userName: 'You',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50',
      message: newMessage.trim(),
      timestamp: new Date().toISOString(),
      isBlocked: false,
      isOwn: true
    };
    
    setChatMessages(prev => [...prev, msg]);
    setNewMessage('');
    toast.success('Message sent');
  }, [newMessage]);

  // Add tip system message to chat
  const addTipSystemMessage = useCallback((tip) => {
    const systemMsg = {
      id: `tip-${Date.now()}`,
      userId: 'system',
      userName: 'System',
      avatar: '/exit88-logo.png',
      message: `💰 ${tip.senderName || 'A viewer'} sent $${tip.amount} to ${tip.artistName}!`,
      timestamp: new Date().toISOString(),
      isSystem: true,
      isBlocked: false
    };
    setChatMessages(prev => [...prev, systemMsg]);
  }, []);

  // Handle tip sent - inject into chat
  useEffect(() => {
    if (onTipSent) {
      // This will be called from parent
    }
  }, [onTipSent]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && videoContainerRef.current) {
      videoContainerRef.current.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  return (
    <div className="h-screen bg-black overflow-hidden" data-testid="livestream-live">
      {/* === MOBILE LAYOUT: Video fullscreen with chat overlay === */}
      <div className="lg:hidden relative h-full">
        {/* Video Background - Full screen */}
        <div className="absolute inset-0">
          <img 
            src={event.coverImage || event.image} 
            alt={event.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        </div>

        {/* LIVE Badge & Viewer Count - Top */}
        <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
          <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">LIVE</span>
          <span className="px-2 py-1 bg-black/60 text-white text-xs rounded flex items-center gap-1">
            <Users className="w-3 h-3" /> {event.viewers?.toLocaleString() || '2,547'}
          </span>
        </div>

        {/* Back Button - Top Right */}
        <button 
          onClick={() => navigate(-1)}
          className="absolute top-4 right-4 z-10 p-2 bg-black/40 rounded-full text-white"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Chat Overlay - LEFT side, above video */}
        <div className="absolute bottom-36 left-0 right-20 max-h-[40vh] flex flex-col z-10">
          {/* Chat Messages */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto px-4 space-y-2 scrollbar-hide"
            data-testid="chat-messages-mobile"
          >
            {chatMessages.slice(-10).map((msg) => (
              <div 
                key={msg.id} 
                className={`${msg.isSystem ? 'bg-gradient-to-r from-yellow-500/30 to-orange-500/30 rounded-lg px-3 py-1.5' : ''}`}
              >
                {msg.isSystem ? (
                  <span className="text-sm text-yellow-300">{msg.message}</span>
                ) : (
                  <div className="flex gap-2 items-start">
                    <img 
                      src={msg.avatar} 
                      alt={msg.userName}
                      className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      <span className="text-primary text-xs font-medium mr-2">{msg.userName}</span>
                      <span className="text-white text-sm">{msg.message}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons - RIGHT side */}
        <div className="absolute bottom-40 right-4 flex flex-col gap-4 z-10">
          <button className="flex flex-col items-center">
            <div className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-white mt-1">1.2K</span>
          </button>
          <button className="flex flex-col items-center" onClick={() => setShowChat(!showChat)}>
            <div className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-white mt-1">Chat</span>
          </button>
          <button className="flex flex-col items-center">
            <div className="w-10 h-10 bg-black/40 rounded-full flex items-center justify-center">
              <Share2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-xs text-white mt-1">Share</span>
          </button>
        </div>

        {/* Bottom Bar - Chat Input + Tip Button - with safe area spacing */}
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-28 z-10 bg-gradient-to-t from-black/90 to-transparent pt-8">
          <div className="flex gap-2 items-center">
            <div className="flex-1 relative">
              <Input 
                type="text"
                placeholder="Send a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 pr-10 h-10"
                data-testid="chat-input-mobile"
              />
              <button 
                onClick={handleSendMessage}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-primary"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            <Button 
              onClick={onOpenTipSheet}
              className="h-10 px-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold"
              data-testid="tip-artist-btn-mobile"
            >
              <DollarSign className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* === DESKTOP LAYOUT: Split view with chat on left, video on right === */}
      <div className="hidden lg:flex h-full w-full">
        {/* Chat Sidebar - LEFT on desktop */}
        <div 
          className="flex flex-col w-96 bg-card border-r border-border"
          data-testid="chat-sidebar"
        >
        {/* Chat Header */}
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">Live Chat</h3>
            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{event.title}</p>
          </div>
          <button 
            className="lg:hidden p-2 hover:bg-secondary rounded-lg"
            onClick={() => setShowChat(false)}
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Chat Messages */}
        <div 
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-3"
          data-testid="chat-messages"
        >
          {chatMessages.map((msg) => (
            <div key={msg.id} className={`flex gap-2 ${msg.isOwn ? 'flex-row-reverse' : ''} ${msg.isSystem ? 'justify-center' : ''}`}>
              {msg.isSystem ? (
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 px-4 py-2 rounded-full">
                  <span className="text-sm text-yellow-300">{msg.message}</span>
                </div>
              ) : (
                <>
                  <img 
                    src={msg.avatar} 
                    alt={msg.userName}
                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  />
                  <div className={`flex-1 ${msg.isOwn ? 'text-right' : ''}`}>
                    <div className={`flex items-baseline gap-2 ${msg.isOwn ? 'justify-end' : ''}`}>
                      <span className={`text-sm font-medium ${msg.isOwn ? 'text-primary' : 'text-primary'}`}>
                        {msg.userName}
                      </span>
                    </div>
                    <p className={`text-sm text-white ${msg.isOwn ? 'bg-primary/20 inline-block px-3 py-1 rounded-lg' : ''}`}>
                      {msg.message}
                    </p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Chat Input + Actions */}
        <div className="p-4 border-t border-border space-y-3">
          <div className="flex gap-2">
            <Input 
              type="text"
              placeholder="Send a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1 bg-secondary border-border text-white placeholder:text-muted-foreground"
              data-testid="chat-input"
            />
            <Button 
              onClick={handleSendMessage}
              className="bg-primary text-primary-foreground px-3"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>

          {/* Tip Button */}
          <Button 
            onClick={onOpenTipSheet}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold h-11"
            data-testid="tip-artist-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Tip Artist
          </Button>
        </div>
        </div>

        {/* Video Player Area - RIGHT side on desktop */}
        <div 
          ref={videoContainerRef}
          className="relative flex-1"
        >
        {/* Video Background / Placeholder */}
        <div className="absolute inset-0">
          <img 
            src={event.coverImage || event.image} 
            alt={event.title}
            className="w-full h-full object-cover opacity-80"
          />
          {!isPlaying && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <button 
                className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center hover:scale-110 transition-transform"
                onClick={() => setIsPlaying(true)}
                data-testid="play-button"
              >
                <Play className="w-10 h-10 text-primary-foreground ml-1" fill="currentColor" />
              </button>
            </div>
          )}
        </div>

        {/* Top Bar - Back button and badges */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10 bg-gradient-to-b from-black/60 to-transparent">
          <button 
            onClick={() => navigate(-1)} 
            className="p-2 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-red-600 text-white text-xs font-semibold rounded-full flex items-center gap-1" data-testid="live-badge">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              LIVE
            </span>
            <span className="px-3 py-1 bg-black/50 text-white text-xs rounded-full flex items-center gap-1" data-testid="viewer-count">
              <Users className="w-3.5 h-3.5" />
              {(2547).toLocaleString()} watching
            </span>
          </div>
        </div>

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent z-10">
          <div className="flex items-center justify-between gap-4">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              <button 
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={() => setIsPlaying(!isPlaying)}
                data-testid="play-pause-btn"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
              </button>
              <button 
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={() => setIsMuted(!isMuted)}
                data-testid="mute-btn"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>

            {/* Center - Event info (mobile) */}
            <div className="flex-1 lg:hidden text-center">
              <p className="text-white text-sm font-medium truncate">{event.title}</p>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              <button
                className="lg:hidden w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={() => setShowChat(!showChat)}
                data-testid="toggle-chat-btn"
              >
                <MessageCircle className="w-5 h-5" />
              </button>
              <button 
                className="hidden lg:flex w-10 h-10 rounded-full bg-white/20 items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={toggleFullscreen}
                data-testid="fullscreen-btn"
              >
                {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// ENDED State Component
const EndedState = ({ event, onOpenTipSheet }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="livestream-ended">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-secondary rounded-lg">
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white truncate">{event.title}</h1>
            <p className="text-xs text-muted-foreground">Event Ended</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-16 pb-20 lg:pb-8">
        <div className="container mx-auto px-4 py-8">
          {/* Video Recap Area */}
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-gray-900">
              <img 
                src={event.coverImage || event.image} 
                alt={event.title}
                className="w-full h-full object-cover opacity-60"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform cursor-pointer">
                    <Play className="w-10 h-10 text-primary-foreground ml-1" fill="currentColor" />
                  </div>
                  <p className="text-white text-lg font-semibold">Watch Replay</p>
                  <p className="text-muted-foreground text-sm mt-1">Full event recording available</p>
                </div>
              </div>

              {/* Ended Badge */}
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-gray-600 text-white text-xs font-semibold rounded-full">
                  ENDED
                </span>
              </div>
            </div>

            {/* Event Info */}
            <div className="mt-6 bg-card border border-border rounded-xl p-6">
              <h2 className="text-xl font-bold text-white mb-2">{event.title}</h2>
              <p className="text-muted-foreground mb-4">{event.description}</p>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-4 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="text-white font-medium">{formatDate(event.date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Venue</p>
                  <p className="text-white font-medium">{event.venue}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="text-white font-medium">~2 hours</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Views</p>
                  <p className="text-white font-medium">{event.views?.toLocaleString() || '—'}</p>
                </div>
              </div>

              {/* Artists */}
              {event.artists && event.artists.length > 0 && (
                <div className="pt-4 border-t border-border">
                  <h3 className="text-sm font-medium text-muted-foreground mb-3">Featured Artists</h3>
                  <div className="flex gap-4 flex-wrap">
                    {event.artists.map(artist => (
                      <Link key={artist.id} to={`/artists/${artist.id}`} className="flex items-center gap-2 group">
                        <img 
                          src={artist.image} 
                          alt={artist.name}
                          className="w-10 h-10 rounded-full object-cover border-2 border-border group-hover:border-primary transition-colors"
                        />
                        <span className="text-white text-sm group-hover:text-primary transition-colors">{artist.name}</span>
                      </Link>
                    ))}
                  </div>
                  
                  {/* Tip Artists Button */}
                  <Button 
                    onClick={onOpenTipSheet}
                    className="mt-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold"
                    data-testid="tip-artist-btn-ended"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Tip the Artists
                  </Button>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button 
                className="flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={() => navigate(`/events/${event.id}/recap`)}
                data-testid="view-recap-btn"
              >
                View Full Recap
              </Button>
              <Button 
                variant="outline"
                className="flex-1 h-12 border-border text-white hover:bg-secondary"
                onClick={() => navigate('/')}
                data-testid="browse-events-btn"
              >
                Browse More Events
              </Button>
            </div>
          </div>
        </div>
      </main>

      <MobileBottomNav activePath="tickets" />
    </div>
  );
};

// Main LivestreamView Component
export function LivestreamView() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [showTipSheet, setShowTipSheet] = useState(false);
  const [forcedStatus, setForcedStatus] = useState(null);
  const [currentStatus, setCurrentStatus] = useState('UPCOMING');
  
  // Find the event
  const event = mockEvents.find(e => e.id === eventId);
  
  // Check DEBUG_UI mode
  const isDebugMode = getDebugUI();

  // Calculate status based on time or forced status
  useEffect(() => {
    if (!event) return;

    const updateStatus = () => {
      const status = getLivestreamStatus(event.date, event.endDate, forcedStatus);
      setCurrentStatus(status);
    };

    updateStatus();
    
    // Update every second if not forced
    if (!forcedStatus) {
      const timer = setInterval(updateStatus, 1000);
      return () => clearInterval(timer);
    }
  }, [event, forcedStatus]);

  // Handle event not found
  if (!event) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-white mb-2">Event Not Found</h2>
          <p className="text-muted-foreground mb-4">The livestream you&apos;re looking for doesn&apos;t exist.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  // Handle tip sent - inject into chat
  const handleTipSent = (tip) => {
    toast.success(`Tip of $${tip.amount} sent to ${tip.artistName}!`);
    // Could inject into chat here if needed
  };

  return (
    <>
      {/* Debug UI Panel */}
      <DebugUIPanel 
        isVisible={isDebugMode}
        forcedStatus={forcedStatus}
        onStatusChange={setForcedStatus}
      />

      {/* Render appropriate state */}
      {currentStatus === 'UPCOMING' && (
        <UpcomingState event={event} timeLeft={getTimeRemaining(event.date)} />
      )}
      
      {currentStatus === 'LIVE' && (
        <LiveState event={event} onOpenTipSheet={() => setShowTipSheet(true)} />
      )}
      
      {currentStatus === 'ENDED' && (
        <EndedState event={event} onOpenTipSheet={() => setShowTipSheet(true)} />
      )}

      {/* Tip Artist Sheet */}
      <TipArtistSheet 
        isOpen={showTipSheet}
        onClose={() => setShowTipSheet(false)}
        eventId={eventId}
        eventArtists={event.artists || []}
        onTipSent={handleTipSent}
      />
    </>
  );
}

export default LivestreamView;
